import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ProtectedRoute } from './components/ProtectedRoute';

// Login stays eager so the landing page renders without a second fetch
import Login from './pages/admin/Login';

// All other pages are lazy-loaded: each route only downloads its own chunk.
// This matters most for OBS browser sources, which never need the admin code.

// OBS Pages
const Results = lazy(() => import('./pages/obs/Results'));
const CardReveal = lazy(() => import('./pages/obs/CardReveal'));
const ShopifyAlert = lazy(() => import('./pages/obs/ShopifyAlert'));
const StreamOverlayOutput = lazy(() => import('./pages/obs/StreamOverlayOutput'));
const GameRevolver = lazy(() => import('./pages/obs/GameRevolver'));
const OverlayLoader = lazy(() => import('./pages/obs/OverlayLoader'));
const WheelOverlayLoader = lazy(() => import('./pages/obs/WheelOverlayLoader'));
const OverlayPreview = lazy(() => import('./pages/obs/OverlayPreview'));

// Admin Pages
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const Profile = lazy(() => import('./pages/admin/Profile'));
const WebhookManager = lazy(() => import('./pages/admin/WebhookManager'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const StreamOverlay = lazy(() => import('./pages/admin/StreamOverlay'));
const Exclusions = lazy(() => import('./pages/admin/Exclusions'));
const Adjustments = lazy(() => import('./pages/admin/Adjustments'));
const ObsSources = lazy(() => import('./pages/admin/ObsSources'));
const GameRevolverEditor = lazy(() => import('./pages/admin/GameRevolverEditor'));
const ApiTokens = lazy(() => import('./pages/admin/ApiTokens'));
const OverlayDetail = lazy(() => import('./pages/admin/OverlayDetail'));
const WheelDetail = lazy(() => import('./pages/admin/WheelDetail'));
const WheelTypeDetail = lazy(() => import('./pages/admin/WheelTypeDetail'));

// Empty fallback: OBS sources must stay transparent while loading, and admin
// chunks load fast enough that a flash of spinner would be worse than nothing.
const suspend = (node: ReactNode) => <Suspense fallback={null}>{node}</Suspense>;

// Using the data-router API (createBrowserRouter) so hooks like useBlocker
// work for navigation guards. The contexts still wrap RouterProvider.
const router = createBrowserRouter([
  // OBS Browser Source Routes - Public
  { path: '/obs/results', element: suspend(<Results />) },
  { path: '/obs/card-reveal', element: suspend(<CardReveal />) },
  { path: '/obs/shopify-alert', element: suspend(<ShopifyAlert />) },
  { path: '/obs/stream-overlay', element: suspend(<StreamOverlayOutput />) },
  { path: '/obs/game-revolver', element: suspend(<GameRevolver />) },

  // Admin preview harness (rendered inside LivePreviewPane iframe)
  { path: '/obs/preview/:schemaKey', element: suspend(<OverlayPreview />) },

  // Custom wheel overlay routes
  { path: '/obs/wheel/:slug', element: suspend(<WheelOverlayLoader />) },
  { path: '/obs/wheel/i/:wheelId', element: suspend(<WheelOverlayLoader />) },

  // Overlay instance routes (UUID and slug-based)
  { path: '/obs/i/:instanceId', element: suspend(<OverlayLoader />) },
  { path: '/obs/:slug', element: suspend(<OverlayLoader />) },

  // Admin login
  { path: '/admin/login', element: <Login /> },

  // Admin: Dashboard
  {
    path: '/admin/dashboard',
    element: suspend(<ProtectedRoute><Dashboard /></ProtectedRoute>),
  },

  // Admin: Overlays
  { path: '/admin/settings', element: <Navigate to="/admin/overlays" replace /> },
  {
    path: '/admin/overlays',
    element: suspend(<ProtectedRoute requireAdmin><OverlayDetail /></ProtectedRoute>),
  },
  {
    path: '/admin/overlays/:schemaKey',
    element: suspend(<ProtectedRoute requireAdmin><OverlayDetail /></ProtectedRoute>),
  },

  // Admin: Wheels
  { path: '/admin/wheel-editor', element: <Navigate to="/admin/wheels" replace /> },
  {
    path: '/admin/wheels',
    element: suspend(<ProtectedRoute><WheelDetail /></ProtectedRoute>),
  },
  {
    path: '/admin/wheels/:id',
    element: suspend(<ProtectedRoute><WheelDetail /></ProtectedRoute>),
  },
  { path: '/admin/wheel-type-settings', element: <Navigate to="/admin/wheels/types" replace /> },
  {
    path: '/admin/wheels/types',
    element: suspend(<ProtectedRoute requireAdmin><WheelTypeDetail /></ProtectedRoute>),
  },
  {
    path: '/admin/wheels/types/:type',
    element: suspend(<ProtectedRoute requireAdmin><WheelTypeDetail /></ProtectedRoute>),
  },

  // Admin: Stream Overlay Canvas + OBS Sources
  {
    path: '/admin/stream-overlay',
    element: suspend(<ProtectedRoute><StreamOverlay /></ProtectedRoute>),
  },
  {
    path: '/admin/obs-sources',
    element: suspend(<ProtectedRoute requireAdmin><ObsSources /></ProtectedRoute>),
  },

  // Admin: Game Revolver
  {
    path: '/admin/game-revolver/:instanceId?',
    element: suspend(<ProtectedRoute requiredPermission="game_revolver"><GameRevolverEditor /></ProtectedRoute>),
  },

  // Admin: Points
  {
    path: '/admin/adjustments',
    element: suspend(<ProtectedRoute requireAdmin><Adjustments /></ProtectedRoute>),
  },
  {
    path: '/admin/exclusions',
    element: suspend(<ProtectedRoute requireAdmin><Exclusions /></ProtectedRoute>),
  },

  // Admin: System
  {
    path: '/admin/webhooks',
    element: suspend(<ProtectedRoute requireAdmin><WebhookManager /></ProtectedRoute>),
  },
  {
    path: '/admin/users',
    element: suspend(<ProtectedRoute requireSuperAdmin><UserManagement /></ProtectedRoute>),
  },

  // Admin: Account
  {
    path: '/admin/profile',
    element: suspend(<ProtectedRoute><Profile /></ProtectedRoute>),
  },
  {
    path: '/admin/api-tokens',
    element: suspend(<ProtectedRoute><ApiTokens /></ProtectedRoute>),
  },

  // Default routes
  { path: '/', element: <Navigate to="/admin/login" replace /> },
  { path: '*', element: <Navigate to="/admin/login" replace /> },
]);

function App() {
  return (
    <AuthProvider>
      <WebSocketProvider>
        <NotificationProvider>
          <RouterProvider router={router} />
        </NotificationProvider>
      </WebSocketProvider>
    </AuthProvider>
  );
}

export default App;
